import {
  PLAY_SETTINGS_CHANGED_EVENT,
  PLAY_SETTINGS_STORAGE_KEY,
  normalizeUiSizeScale,
  readPlaySettingsSnapshot,
} from '../../state/playSettingsState';

// Apply only to live lobby / table DOM, not to the artwork, card faces,
// chips, table layout or auth pages. Works with CSS modules and inline font sizes.
// Includes both in-stage UI and popups portaled directly under document.body.
const SCOPE = '[data-npc-ui-scale-scope]';
const CONTROL = 'button, [role="button"], input:not([type="hidden"]), select, textarea';
// The mobile lobby/table and the mission modal render many visible labels directly
// in DIV/EM/I elements. The previous whitelist skipped those labels entirely.
// Keep the directText check below: structural containers are not enlarged.
const TEXT = 'div, span, strong, b, em, i, small, p, label, legend, summary, code, h1, h2, h3, h4, h5, h6, a, li, dt, dd, td, th, time, output';
const TARGETS = `${CONTROL}, ${TEXT}`;
const isElement = (node) => node && node.nodeType === 1;
const inScope = (node) => isElement(node) && !!node.closest(SCOPE);

function directText(node) {
  for (let child = node.firstChild; child; child = child.nextSibling) {
    if (child.nodeType === 3 && child.textContent.trim()) return true;
  }
  return false;
}

function pxNumber(raw) {
  const found = /^([\d.]+)px$/.exec(String(raw || '').trim());
  return found ? Number(found[1]) : null;
}

/**
 * Accessible local preference. 1x does not modify any element. At 1.1x/1.2x,
 * controls enlarge in layout (CSS zoom); text nodes use their *own* computed
 * baseline font sizes. The table/stage background remains geometrically stable.
 */
export function installUiSizeRuntime() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  let scale = normalizeUiSizeScale(readPlaySettingsSnapshot().uiSizeScale);
  let raf = 0;
  let pendingFull = true;
  const pending = new Set();
  const active = new Map();
  let flushCount = 0;

  function eligible(el) {
    return inScope(el) && !el.closest('[data-npc-ui-scale-exempt]');
  }

  function restore(el, record) {
    if (!isElement(el)) return;
    const prop = record.kind === 'control' ? 'zoom' : 'font-size';
    if (record.oldValue) el.style.setProperty(prop, record.oldValue, record.oldPriority);
    else el.style.removeProperty(prop);
    if (record.kind === 'text') {
      if (record.oldLine) el.style.setProperty('line-height', record.oldLine, record.oldLinePriority);
      else el.style.removeProperty('line-height');
    }
  }

  function capture(el) {
    if (!eligible(el)) return;
    // Full-width mission summary is kept at its approved 300px geometry.
    // Only the text descendants are scaled, not the button's box.
    const isControl = el.matches(CONTROL);
    if (isControl && el.hasAttribute('data-npc-ui-scale-no-zoom')) return;
    if (!isControl) {
      if (!el.matches(TEXT) || !directText(el)) return;
      const parentControl = el.closest(CONTROL);
      if (parentControl && !parentControl.hasAttribute('data-npc-ui-scale-no-zoom')) return;
    }
    if (active.has(el)) return;
    const css = window.getComputedStyle(el);
    const baseSize = pxNumber(css.fontSize);
    if (!isControl && (!baseSize || baseSize < 1)) return;
    const record = isControl ? {
      kind: 'control',
      oldValue: el.style.getPropertyValue('zoom'),
      oldPriority: el.style.getPropertyPriority('zoom'),
      baseZoom: Number(css.zoom) > 0 ? Number(css.zoom) : 1,
    } : {
      kind: 'text', baseSize, baseLine: pxNumber(css.lineHeight),
      oldValue: el.style.getPropertyValue('font-size'),
      oldPriority: el.style.getPropertyPriority('font-size'),
      oldLine: el.style.getPropertyValue('line-height'),
      oldLinePriority: el.style.getPropertyPriority('line-height'),
    };
    active.set(el, record);
  }

  function apply(el, record) {
    if (!eligible(el)) {
      restore(el, record);
      active.delete(el);
      return;
    }
    if (scale === 1) {
      restore(el, record);
      active.delete(el);
      return;
    }
    if (record.kind === 'control') {
      // Zoom only the interactive control, not its parent stage. A 1.1x/1.2x
      // multiplier grows touch targets without scaling the table artwork.
      const zoom = String(Number((record.baseZoom * scale).toFixed(3)));
      if (el.style.getPropertyValue('zoom') !== zoom || el.style.getPropertyPriority('zoom') !== 'important') {
        el.style.setProperty('zoom', zoom, 'important');
      }
    } else {
      const fontSize = `${Number((record.baseSize * scale).toFixed(2))}px`;
      if (el.style.getPropertyValue('font-size') !== fontSize || el.style.getPropertyPriority('font-size') !== 'important') {
        el.style.setProperty('font-size', fontSize, 'important');
      }
      if (record.baseLine !== null) {
        const lineHeight = `${Number((record.baseLine * scale).toFixed(2))}px`;
        if (el.style.getPropertyValue('line-height') !== lineHeight || el.style.getPropertyPriority('line-height') !== 'important') {
          el.style.setProperty('line-height', lineHeight, 'important');
        }
      }
    }
  }

  function findAndApply(root) {
    if (!isElement(root)) return;
    const nodes = root.matches(TARGETS) ? [root, ...root.querySelectorAll(TARGETS)] : root.querySelectorAll(TARGETS);
    // Collect baselines before changing ancestors (prevents inheritance compounding).
    for (const el of nodes) {
      if (!active.has(el) && eligible(el)) capture(el);
    }
    for (const el of nodes) {
      const record = active.get(el);
      if (record) apply(el, record);
    }
  }

  function flush() {
    raf = 0;
    if (pendingFull) {
      pendingFull = false;
      if (scale === 1) {
        for (const [el, record] of active) restore(el, record);
        active.clear();
      } else {
        document.querySelectorAll(SCOPE).forEach((root) => findAndApply(root));
      }
    } else if (scale !== 1) {
      for (const added of pending) {
        if (added.isConnected) {
          if (added.matches(SCOPE)) findAndApply(added);
          else if (added.closest(SCOPE)) findAndApply(added);
          else added.querySelectorAll(SCOPE).forEach((root) => findAndApply(root));
        }
      }
    }
    pending.clear();
    // Periodically forget detached nodes (table close/navigation) without
    // an O(N) scan on every frequent table-state animation tick.
    if (++flushCount % 100 === 0) {
      for (const [el] of active) if (!el.isConnected) active.delete(el);
    }
  }

  function schedule() { if (!raf) raf = window.requestAnimationFrame(flush); }
  function changed() {
    const next = normalizeUiSizeScale(readPlaySettingsSnapshot().uiSizeScale);
    if (next === scale) return;
    scale = next;
    pendingFull = true;
    schedule();
  }
  function stored(ev) {
    if (!ev.key || ev.key === PLAY_SETTINGS_STORAGE_KEY) changed();
  }

  const observer = new MutationObserver((mutations) => {
    if (scale === 1) return;
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (isElement(node)) pending.add(node);
        }
      } else if (mutation.type === 'attributes') {
        const el = mutation.target;
        // React can replace an inline font-size/zoom after an update. Re-apply
        // a previously captured baseline, without rescanning the whole table.
        if (!active.has(el) || !eligible(el)) continue;
        const record = active.get(el);
        const prop = record.kind === 'control' ? 'zoom' : 'font-size';
        const expected = record.kind === 'control'
          ? String(Number((record.baseZoom * scale).toFixed(3)))
          : `${Number((record.baseSize * scale).toFixed(2))}px`;
        if (el.style.getPropertyValue(prop) !== expected || el.style.getPropertyPriority(prop) !== 'important') {
          pending.add(el);
        }
      }
    }
    if (pending.size) schedule();
  });
  const start = () => {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, attributes: true, attributeFilter: ['style'], subtree: true });
    schedule();
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
  window.addEventListener(PLAY_SETTINGS_CHANGED_EVENT, changed);
  window.addEventListener('storage', stored);

  return () => {
    observer.disconnect();
    document.removeEventListener('DOMContentLoaded', start);
    window.removeEventListener(PLAY_SETTINGS_CHANGED_EVENT, changed);
    window.removeEventListener('storage', stored);
    if (raf) window.cancelAnimationFrame(raf);
    for (const [el, record] of active) restore(el, record);
    active.clear();
    pending.clear();
  };
}
